import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Upload, Trash2, CheckCircle2, FileText, DollarSign } from "lucide-react";
import PoweredBy from "@/components/PoweredBy";
import RefundCalculator from "@/components/RefundCalculator";

const CATEGORY_LABELS = {
  original_return: "Original return (last year's 1040)",
  corrected_document: "Corrected / new document (W-2c, corrected 1099, etc.)",
  supporting_docs: "Other supporting docs (letters, statements)",
};

export default function Amend() {
  const { branding } = useBranding();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingCat, setUploadingCat] = useState(null);
  const [done, setDone] = useState(null);

  const [f, setF] = useState({
    tax_year: 2024,
    reason: "",
    description: "",
    first_name: "", last_name: "", email: "", phone: "", ssn: "",
    uploads: {},
    fee_ack: false,
    signature: "", signed_date: new Date().toISOString().slice(0, 10),
    notes: "",
    referrer_ref: "",
  });

  // Capture ?ref= exactly once on mount — carries through the submission.
  useEffect(() => {
    const ref = (searchParams.get("ref") || "").trim().slice(0, 120);
    if (ref) setF(prev => ({ ...prev, referrer_ref: ref }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/amendment/config").then(r => {
      setConfig(r.data);
      // Keep tax_year within the valid range the backend just returned.
      setF(prev => {
        const mn = r.data?.min_year || 2022;
        const mx = r.data?.max_year || 2026;
        const y = Number(prev.tax_year);
        if (y < mn || y > mx) return { ...prev, tax_year: mx };
        return prev;
      });
    }).catch(() => {});
  }, []);

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const uploadFiles = async (category, filelist) => {
    if (!filelist?.length) return;
    setUploadingCat(category);
    const added = [];
    for (const file of Array.from(filelist)) {
      if (file.size > 20 * 1024 * 1024) { toast.error(`${file.name}: file too large (20 MB max)`); continue; }
      const form = new FormData();
      form.append("file", file);
      try {
        const r = await api.post(`/amendment/upload?category=${category}`, form, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(r.data.path);
      } catch (e) { toast.error(`${file.name}: ${e?.response?.data?.detail || "upload failed"}`); }
    }
    if (added.length) {
      setF(prev => ({ ...prev, uploads: { ...prev.uploads, [category]: [...(prev.uploads[category] || []), ...added] } }));
      toast.success(`${added.length} file${added.length === 1 ? "" : "s"} uploaded`);
    }
    setUploadingCat(null);
  };

  const removeUpload = (category, idx) => {
    setF(prev => ({ ...prev, uploads: { ...prev.uploads, [category]: (prev.uploads[category] || []).filter((_, i) => i !== idx) } }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.fee_ack) { toast.error(`Please acknowledge the $${config?.fee || 175} amendment fee.`); return; }
    if (!f.reason) { toast.error("Please pick a reason for the amendment."); return; }
    if (!f.signature.trim()) { toast.error("Please type your name to sign."); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/amendment", { ...f, tax_year: Number(f.tax_year) });
      setDone({ id: r.data.id, access_code: r.data.access_code });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Submission failed"); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background paper-texture p-6 py-12">
        <div className="max-w-2xl mx-auto space-y-8">
          <div className="bg-card border border-border rounded-md p-10 text-center" data-testid="amend-success">
            <CheckCircle2 size={56} className="mx-auto text-primary mb-4" />
            <div className="label-caps mb-2">Received</div>
            <h1 className="font-serif text-4xl mb-3">Thanks, {f.first_name}!</h1>
            <p className="text-muted-foreground mb-6">
              Your amendment request is in. A preparer will review and reach out with next steps within 1-2 business days. The ${config?.fee || 175} fee is billed once we start work.
            </p>
            <div className="rounded-md border border-primary/40 bg-primary/5 p-4 mb-6 text-left">
              <div className="label-caps text-[10px] mb-2 text-primary">Save these · to check status</div>
              <div className="space-y-1 text-sm">
                <div>Reference #: <span className="font-mono">{done.id.slice(0, 8)}</span></div>
                <div>Access code: <span className="font-mono text-lg tracking-widest" data-testid="amend-access-code">{done.access_code}</span></div>
                <div className="text-xs text-muted-foreground pt-1">Email on file: {f.email}</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-6">
              Track progress at <Link to="/client-portal" className="underline text-foreground">the client portal</Link>.
            </p>
            <Button onClick={() => nav("/")} className="w-full" data-testid="amend-done-btn">Back to homepage</Button>
          </div>

          <div>
            <div className="label-caps text-primary mb-3 text-center">Estimate the new refund after the amendment</div>
            <RefundCalculator compact context="amend_success" />
          </div>
        </div>
      </div>
    );
  }

  const years = [];
  const min = config?.min_year || 2021, max = config?.max_year || 2024;
  for (let y = max; y >= min; y--) years.push(y);

  return (
    <div className="min-h-screen bg-background paper-texture">
      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" /> :
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">{branding.brand_name?.[0]}</div>}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <div className="label-caps hidden sm:block">Amendment</div>
        </div>
      </header>

      <form onSubmit={submit} className="max-w-3xl mx-auto p-6 lg:p-12 space-y-8" data-testid="amend-form">
        <div>
          <div className="label-caps mb-2">Prior-year amendment · 1040-X</div>
          <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-3">Fix a prior return.</h1>
          <p className="text-muted-foreground max-w-xl">
            Got a corrected W-2? Missed a credit? We'll amend a return we filed — or one you filed yourself — for a flat <strong>${config?.fee || 175}</strong> per return.
          </p>
        </div>

        {/* Pricing banner — highlights the discount cutoff */}
        {config && (
          <Card className={`p-5 border-2 ${config.is_discount_active ? "border-primary/40 bg-primary/5" : "border-border"}`} data-testid="amend-pricing-banner">
            <div className="flex flex-wrap items-center gap-4 justify-between">
              <div>
                <div className="label-caps text-[10px] text-primary mb-1">
                  {config.is_discount_active ? "Early bird pricing" : "Standard pricing"}
                </div>
                <div className="font-serif text-3xl leading-none">
                  ${config.fee}
                  <span className="text-sm text-muted-foreground font-sans font-normal ml-2">per amended return</span>
                </div>
                {config.is_discount_active && (
                  <div className="text-xs text-muted-foreground mt-2">
                    Discounted <strong className="text-foreground">${config.discount_fee}</strong> through <strong className="text-foreground">October 15, {new Date(config.discount_deadline).getUTCFullYear()}</strong> · goes to <strong className="text-foreground">${config.regular_fee}</strong> after.
                  </div>
                )}
                {!config.is_discount_active && (
                  <div className="text-xs text-muted-foreground mt-2">
                    Standard rate. Watch for our next early-bird window.
                  </div>
                )}
              </div>
              <div className="text-right">
                <DollarSign size={24} className="text-primary inline" />
              </div>
            </div>
          </Card>
        )}

        {/* Amendment specifics */}
        <Section title="What's being amended">
          <Row>
            <Field label="Tax year" required>
              <select
                required
                data-testid="amend-year"
                value={f.tax_year}
                onChange={e => set("tax_year", e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </Field>
            <Field label="Reason" required>
              <select
                required
                data-testid="amend-reason"
                value={f.reason}
                onChange={e => set("reason", e.target.value)}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Choose one…</option>
                {Object.entries(config?.reasons || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
          </Row>
          <Field label="Describe what needs to change">
            <Textarea rows={3} value={f.description} onChange={e => set("description", e.target.value)} placeholder="e.g. My employer sent a W-2c after I filed. My box 1 wages went from $42,300 to $44,880." data-testid="amend-description" />
          </Field>
        </Section>

        {/* Client info */}
        <Section title="Your info">
          <Row>
            <Field label="First name" required><Input required value={f.first_name} onChange={e => set("first_name", e.target.value)} data-testid="amend-first-name" /></Field>
            <Field label="Last name" required><Input required value={f.last_name} onChange={e => set("last_name", e.target.value)} data-testid="amend-last-name" /></Field>
          </Row>
          <Row>
            <Field label="Email" required><Input required type="email" value={f.email} onChange={e => set("email", e.target.value)} data-testid="amend-email" /></Field>
            <Field label="Phone"><Input type="tel" value={f.phone} onChange={e => set("phone", e.target.value)} /></Field>
          </Row>
          <Field label="SSN on the return being amended">
            <Input placeholder="XXX-XX-XXXX" value={f.ssn} onChange={e => set("ssn", e.target.value)} data-testid="amend-ssn" />
          </Field>
        </Section>

        {/* Uploads */}
        <Section title="Upload documents">
          <p className="text-sm text-muted-foreground -mt-2 mb-3">
            Upload the original return you filed, the corrected/new document (W-2c, corrected 1099, etc.), and anything else that helps. PDF/PNG/JPG/HEIC, 20 MB per file.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {(config?.categories || []).map((key) => (
              <Card key={key} className="p-4">
                <div className="text-sm font-medium mb-1">{CATEGORY_LABELS[key] || key}</div>
                <div className="text-[10px] text-muted-foreground mb-2">
                  {(f.uploads[key] || []).length} file{(f.uploads[key] || []).length === 1 ? "" : "s"} attached
                </div>
                <label className="inline-flex w-full">
                  <input
                    type="file"
                    multiple
                    accept="application/pdf,image/*"
                    className="hidden"
                    data-testid={`amend-upload-${key}`}
                    onChange={e => uploadFiles(key, e.target.files)}
                  />
                  <Button asChild size="sm" variant="outline" className="w-full" disabled={uploadingCat === key}>
                    <span><Upload size={12} className="mr-1" /> {uploadingCat === key ? "Uploading…" : "Choose files"}</span>
                  </Button>
                </label>
                {(f.uploads[key] || []).map((p, idx) => (
                  <div key={idx} className="text-[10px] font-mono mt-1 flex items-center justify-between gap-2">
                    <span className="truncate min-w-0"><FileText size={10} className="inline mr-1" />…{p.slice(-24)}</span>
                    <button type="button" onClick={() => removeUpload(key, idx)} className="text-destructive" aria-label="Remove"><Trash2 size={10} /></button>
                  </div>
                ))}
              </Card>
            ))}
          </div>
        </Section>

        {/* Notes */}
        <Section title="Anything else?">
          <Textarea rows={2} value={f.notes} onChange={e => set("notes", e.target.value)} placeholder="Optional — anything that helps us process the amendment faster." />
        </Section>

        {/* Fee acknowledgment + signature */}
        <Section title="Fee acknowledgment & signature">
          <div className="rounded-md bg-secondary/40 border border-border p-4 text-sm">
            <label className="flex items-start gap-3">
              <input type="checkbox" checked={f.fee_ack} onChange={e => set("fee_ack", e.target.checked)} data-testid="amend-fee-ack" className="mt-1" />
              <span className="flex-1">
                <span className="flex items-center gap-1 font-medium mb-1"><DollarSign size={12} /> ${config?.fee || 175} amendment fee</span>
                <span className="text-muted-foreground text-xs">
                  I understand the flat fee to file a 1040-X amendment through {branding.brand_name} is <strong>${config?.fee || 175}</strong> per return, billed once work begins. This fee does not include any additional tax owed to the IRS.
                </span>
              </span>
            </label>
          </div>
          <Row>
            <Field label="Type your full name to sign" required>
              <Input required data-testid="amend-signature" value={f.signature} onChange={e => set("signature", e.target.value)} placeholder="Your legal name" />
            </Field>
            <Field label="Date"><Input type="date" value={f.signed_date} onChange={e => set("signed_date", e.target.value)} /></Field>
          </Row>
        </Section>

        <div className="flex flex-wrap justify-between gap-3 pt-4 border-t border-border">
          <Button type="button" variant="ghost" onClick={() => nav("/")}>Back</Button>
          <Button type="submit" size="lg" disabled={submitting} data-testid="amend-submit-btn">
            {submitting ? "Submitting…" : "Submit amendment request"}
          </Button>
        </div>

        <PoweredBy className="text-center mt-6" />
      </form>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="space-y-4">
    <div className="border-b border-border pb-2"><h2 className="font-serif text-2xl">{title}</h2></div>
    {children}
  </section>
);
const Row = ({ children }) => <div className="grid sm:grid-cols-2 gap-3">{children}</div>;
const Field = ({ label, required, children }) => (
  <div className="space-y-1">
    <Label className="label-caps text-[10px]">{label}{required && <span className="text-destructive ml-0.5">*</span>}</Label>
    {children}
  </div>
);
